import { useEffect, useMemo, useRef, useState } from "react";
import { Headphones, Play, Square, Volume2, Music, ArrowLeftRight } from "lucide-react";
import {
  SOUND_LIBRARY,
  CATEGORIES,
  EVIDENCE_LABELS,
  EVIDENCE_STYLE,
  STUDY_STATUS_LABELS,
  STUDY_STATUS_STYLE,
  freqToNote,
  type SoundEntry,
} from "@/components/soundboard/types";
import { DemoSoundEngine, SAMPLE_SECONDS } from "../demo/demo-sound-engine";
import { useSyncedState } from "../demo/live/useSyncedState";
import { track } from "../demo/analytics";
import { cn } from "@/lib/utils";

/**
 * DemoSoundboard (/soundboard) — the Therapeutic Soundboard as a playable example.
 * Every sound plays a real ~20s synthesized sample (binaural / isochronic / pure
 * tone / noise via DemoSoundEngine) so a prospect can hear it, with the same
 * evidence-honest badges the real product shows. Static, in-memory, no backend.
 */
export function DemoSoundboard() {
  const engineRef = useRef<DemoSoundEngine | null>(null);
  if (!engineRef.current) engineRef.current = new DemoSoundEngine();
  const engine = engineRef.current;

  // Co-browsed so the guest follows the presenter's category tab + audio controls.
  // playingId/progress stay LOCAL: audio is device-local (autoplay needs a tap on the
  // guest), so mirroring "which sample is playing" without sound would mislead — a
  // follow-up can drive the guest's own engine from a synced playingId if wanted.
  const [cat, setCat] = useSyncedState<string>("sb.cat", "All");
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0); // 0..1 across the sample
  const [volume, setVolume] = useSyncedState("sb.volume", 0.3);
  const [panHz, setPanHz] = useSyncedState("sb.panHz", 0.6); // bilateral L↔R sweep rate
  const [unsupported, setUnsupported] = useState(false);
  const progressTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tear down the AudioContext + any ticking timer when leaving the page.
  useEffect(() => {
    const e = engine;
    return () => {
      if (progressTimer.current) clearInterval(progressTimer.current);
      e.destroy();
    };
  }, [engine]);

  const shown = useMemo(() => SOUND_LIBRARY.filter((s) => cat === "All" || s.category === cat), [cat]);
  const playing = playingId ? SOUND_LIBRARY.find((s) => s.id === playingId) ?? null : null;

  function stopPlayback() {
    engine.stop();
    setPlayingId(null);
    setProgress(0);
    if (progressTimer.current) clearInterval(progressTimer.current);
  }

  function toggle(sound: SoundEntry) {
    if (playingId === sound.id) {
      stopPlayback();
      return;
    }
    if (progressTimer.current) clearInterval(progressTimer.current);
    const ok = engine.play(sound, () => {
      // Sample finished on its own.
      setPlayingId(null);
      setProgress(0);
      if (progressTimer.current) clearInterval(progressTimer.current);
    });
    if (!ok) {
      setUnsupported(true);
      return;
    }
    track("soundboard_sample_played", { id: sound.id });
    setPlayingId(sound.id);
    setProgress(0);
    const start = performance.now();
    progressTimer.current = setInterval(() => {
      const p = Math.min(1, (performance.now() - start) / (SAMPLE_SECONDS * 1000));
      setProgress(p);
      if (p >= 1 && progressTimer.current) clearInterval(progressTimer.current);
    }, 80);
  }

  function changeVolume(v: number) {
    setVolume(v);
    engine.setVolume(v);
  }

  function changePanHz(v: number) {
    setPanHz(v);
    engine.setPanHz(v);
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--color-gold-soft)]">Soundboard</p>
          <h1 className="mt-1 font-heading text-3xl font-medium text-[var(--color-text-primary)]">Therapeutic Soundboard</h1>
          <p className="mt-1 max-w-xl text-[13px] text-[var(--color-text-muted)]">
            Tap a sound for a {SAMPLE_SECONDS}-second sample, generated live in your browser. Every sound plays <span className="text-[var(--color-gold-soft)]">bilaterally</span> — it sweeps left↔right like the BLS ball — so use headphones, and set the sweep speed below.
          </p>
        </div>
        {/* Volume + bilateral sweep speed */}
        <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-2.5">
          <ControlSlider icon={<Volume2 className="h-4 w-4" />} label="Volume" value={volume} min={0} max={1} step={0.01} fmt={(v) => `${Math.round(v * 100)}%`} onChange={changeVolume} />
          <ControlSlider icon={<ArrowLeftRight className="h-4 w-4" />} label="Bilateral speed" value={panHz} min={0.2} max={2.5} step={0.1} fmt={(v) => `${v.toFixed(1)} Hz`} onChange={changePanHz} />
        </div>
      </header>

      {unsupported && (
        <p className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-4 py-2 text-[12px] text-[var(--color-text-muted)]">
          Your browser blocked Web Audio. Tap a sample again, or try Chrome/Edge — the real app handles this with a one-tap unlock.
        </p>
      )}

      {/* Now-playing banner */}
      {playing && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-[var(--color-border-gold)] bg-[var(--color-navy-light)] px-4 py-3">
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--color-gold-soft)] opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[var(--color-gold-soft)]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-heading text-[14px] font-medium text-[var(--color-text-primary)]">Now playing · {playing.name}</p>
            <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-[var(--color-bg-deep)]">
              <div className="h-full rounded-full bg-[var(--color-gold-soft)] transition-[width] duration-100 ease-linear" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          </div>
          <button
            type="button"
            onClick={stopPlayback}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[var(--color-border-gold)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-gold-soft)] transition-colors hover:bg-[var(--color-navy-mid)]"
          >
            <Square className="h-3 w-3" /> Stop
          </button>
        </div>
      )}

      {/* Category filter */}
      <div className="mt-5 flex flex-wrap gap-1.5">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCat(c)}
            className={cn(
              "rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors",
              cat === c
                ? "border-[var(--color-gold)] bg-[var(--color-navy-light)] text-[var(--color-gold-soft)]"
                : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-gold)]",
            )}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Sound cards */}
      <ul className="mt-4 space-y-2.5">
        {shown.map((s) => {
          const isPlaying = playingId === s.id;
          return (
            <li
              key={s.id}
              className={cn(
                "rounded-xl border bg-[var(--color-navy-mid)] px-4 py-3.5 transition-colors",
                isPlaying ? "border-[var(--color-border-gold)]" : "border-[var(--color-border)]",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-heading text-[15px] font-medium text-[var(--color-text-primary)]">
                    {s.name}
                    {s.requires_headphones && <Headphones className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-ghost)]" />}
                  </p>
                  <p className="text-[12px] text-[var(--color-text-ghost)]">{s.purpose}</p>
                </div>
                <button
                  type="button"
                  onClick={() => toggle(s)}
                  aria-label={isPlaying ? `Stop ${s.name}` : `Play a sample of ${s.name}`}
                  className={cn(
                    "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors",
                    isPlaying
                      ? "border-[var(--color-gold)] bg-[var(--color-gold)] text-[var(--color-bg-deep)]"
                      : "border-[var(--color-border-gold)] text-[var(--color-gold-soft)] hover:bg-[var(--color-navy-light)]",
                  )}
                >
                  {isPlaying ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-[1px]" />}
                </button>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Badge text={s.type} />
                {s.carrierHz != null && (
                  <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                    {s.carrierHz} Hz{s.beatHz != null ? ` · ${s.beatHz} Hz beat` : ` · ${freqToNote(s.carrierHz)}`}
                  </span>
                )}
                <Badge text={STUDY_STATUS_LABELS[s.studyStatus]} style={STUDY_STATUS_STYLE[s.studyStatus]} />
                <Badge text={EVIDENCE_LABELS[s.evidenceStrength]} style={EVIDENCE_STYLE[s.evidenceStrength]} />
              </div>

              <p className="mt-2 text-[12px] leading-relaxed text-[var(--color-text-muted)]">{s.note}</p>

              {isPlaying && (
                <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-[var(--color-bg-deep)]">
                  <div className="h-full rounded-full bg-[var(--color-gold-soft)] transition-[width] duration-100 ease-linear" style={{ width: `${Math.round(progress * 100)}%` }} />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <p className="mt-6 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--color-text-ghost)]">
        <Music className="h-3 w-3" /> Demo samples are generated in your browser · not clinical guidance · the real soundboard ships {"27"}+ curated tracks
      </p>
    </main>
  );
}

function ControlSlider({ icon, label, value, min, max, step, fmt, onChange }: { icon: React.ReactNode; label: string; value: number; min: number; max: number; step: number; fmt: (v: number) => string; onChange: (v: number) => void }) {
  return (
    <label className="flex items-center gap-2.5">
      <span className="text-[var(--color-text-muted)]">{icon}</span>
      <span className="w-[88px] font-mono text-[9px] uppercase tracking-[0.12em] text-[var(--color-text-muted)]">{label}</span>
      <input
        aria-label={label}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1 w-28 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-gold-soft)]"
      />
      <span className="w-12 text-right font-mono text-[11px] text-[var(--color-gold-soft)]">{fmt(value)}</span>
    </label>
  );
}

function Badge({ text, style }: { text: string; style?: { bg: string; text: string } }) {
  return (
    <span
      className="rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide"
      style={
        style
          ? { background: style.bg, color: style.text, borderColor: "transparent" }
          : { borderColor: "var(--color-border)", color: "var(--color-text-ghost)" }
      }
    >
      {text}
    </span>
  );
}
